import { ApiError } from "@nabble/sdk";
import type { Tone } from "@nabble/ui";
import type { DataRequestKind, DataRequestStatus } from "../api/dataRequests";
import { errorMessage } from "../errors";

/** Plain-language names of the rights under Chile Law 21.719, in the order the form offers them. */
export const KIND_LABEL: Record<DataRequestKind, string> = {
  ACCESS: "Access to my data",
  RECTIFICATION: "Correct my data",
  DELETION: "Delete my data",
  OPPOSITION: "Object to a use of my data",
  PORTABILITY: "A portable copy of my data",
  BLOCKING: "Temporarily block my data",
};

/** The same requests, as an administrator reads them about someone else. */
export const KIND_LABEL_ADMIN: Record<DataRequestKind, string> = {
  ACCESS: "Access to their data",
  RECTIFICATION: "Correct their data",
  DELETION: "Delete their data",
  OPPOSITION: "Object to a use of their data",
  PORTABILITY: "A portable copy of their data",
  BLOCKING: "Temporarily block their data",
};

export const KINDS = Object.keys(KIND_LABEL) as DataRequestKind[];

export const STATUS_LABEL: Record<DataRequestStatus, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  DONE: "Done",
  REJECTED: "Not granted",
};

export const STATUS_TONE: Record<DataRequestStatus, Tone> = {
  OPEN: "info",
  IN_PROGRESS: "warning",
  DONE: "success",
  REJECTED: "neutral",
};

export const DETAILS_MAX = 2000;

/** Only a correction needs details: what is wrong and what it should say. */
export function detailsRequired(kind: DataRequestKind): boolean {
  return kind === "RECTIFICATION";
}

export function isAnswered(status: DataRequestStatus): boolean {
  return status === "DONE" || status === "REJECTED";
}

/** Plain message for the errors of the data request endpoints. */
export function dataRequestError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "too_many_open":
        return "You already have 5 open requests. Wait for an answer before making another.";
      case "already_answered":
        return "This request has already been answered. Reload the page to see the answer.";
      case "already_extended":
        return "The deadline of this request has already been extended once. It can't be extended again.";
      case "validation":
        return "Some details are missing or too long. Check the form and try again.";
      default:
        break;
    }
  }
  return errorMessage(err, fallback);
}

export type RequestFilter = "OPEN" | "ANSWERED" | "ALL";

/**
 * The rows of one filter tab: open ones first, by deadline (the soonest on top),
 * then answered ones, the most recently answered first.
 */
export function filterRequests<R extends { status: DataRequestStatus; dueAt: string; resolvedAt: string | null; createdAt: string }>(
  rows: R[],
  filter: RequestFilter,
): R[] {
  const visible = rows.filter((r) => filter === "ALL" || (filter === "ANSWERED") === isAnswered(r.status));
  const time = (v: string | null): number => (v ? new Date(v).getTime() : 0);
  return [...visible].sort((a, b) => {
    const aDone = isAnswered(a.status);
    const bDone = isAnswered(b.status);
    if (aDone !== bDone) return aDone ? 1 : -1;
    if (!aDone) return time(a.dueAt) - time(b.dueAt);
    return time(b.resolvedAt ?? b.createdAt) - time(a.resolvedAt ?? a.createdAt);
  });
}
